import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PanAppState, Tracking } from '@panamax/app-state';
import {
  MY_ORDERS_ACTIONS,
  OrderStatus,
  DeliveryStoreService,
  DidYouForgetStoreService,
  InspiredPicksStoreService,
  OrderDtoService,
  Order,
  CART_ACTIONS,
} from '@usf/ngrx-order';
import { combineLatest, of, zip } from 'rxjs';
import { OrderItemDTOService } from '@usf/ngrx-order';
import {
  tap,
  filter,
  take,
  mergeMap,
  exhaustMap,
  skip,
  map,
  concatMap,
  delay,
} from 'rxjs/operators';
import { selectProductAttributesByIds } from 'src/app/shared/selectors/product.selectors';
import { SharedTrackingService } from 'src/app/shared/services/analytics/shared-tracking.service';
import {
  OrderSubmittedTracingMessages,
  OrderTracingMessagesTest,
} from '../pages/order-submitted/tracking/tracing/order-submitted-tracing.constants';
import { ORDER_ACTIONS } from '@order/actions/order-actions';
import { OrderService } from '@app/order/services/order.service';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { OrderTracingMessages } from '@order/tracking/tracing/order-tracing.constants';
import { Product } from '@usf/product-types';
import { ORDER_TRACING_ACTIONS } from '@order/tracking/tracing/order-tracing.actions';

@Injectable({
  providedIn: 'root',
})
export class OrderHeaderEffects {
  trackSubmitOrderWebsocketResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MY_ORDERS_ACTIONS.submitIpOrderAsync,
        MY_ORDERS_ACTIONS.updateSubmittedOrderAsync,
      ),
      concatMap(webSocketResponse => {
        const websocketMsgContentLength =
          webSocketResponse?.orderTransactions?.length;

        if (websocketMsgContentLength === 0) {
          const submitOrderTraceArr = JSON.parse(
            sessionStorage.getItem('submitOrderTrace'),
          ) || [null, true, false, true];
          const [sessionOrder, isWebSocket, traceDispatched, isEmpty] =
            submitOrderTraceArr;
          sessionStorage.setItem(
            'submitOrderTrace',
            JSON.stringify([sessionOrder, isWebSocket, traceDispatched, true]),
          );
          return [];
        }

        const orderTransaction =
          websocketMsgContentLength === 1
            ? webSocketResponse?.orderTransactions[0]
            : webSocketResponse?.orderTransactions[1];

        const clientOrderItems = this.orderItemDtoService.toClientModelArray(
          orderTransaction.orderItems,
        );
        const clientOrder =
          this.orderDtoService.toClientModel(orderTransaction);
        const order = {
          orderHeader: clientOrder,
          orderItems: clientOrderItems,
        } as Order;
        const submitOrderTraceArr = JSON.parse(
          sessionStorage.getItem('submitOrderTrace'),
        ) || [clientOrder, true, false, false];
        const [sessionOrder, isWebSocket, traceDispatched, isEmpty] =
          submitOrderTraceArr;
        sessionStorage.setItem(
          'submitOrderTrace',
          JSON.stringify([sessionOrder, true, traceDispatched, isEmpty]),
        );
        return [
          ORDER_TRACING_ACTIONS.endOrderSubmissionTrace({
            order,
            isWebsocket: true,
          }),
        ];
      }),
    ),
  );

  trackSubmitOrderResponse$ = createEffect(
    () =>
      zip([
        this.actions$.pipe(
          ofType(
            ORDER_TRACING_ACTIONS.trackSubmitOrderApiResponseSuccess,
            ORDER_TRACING_ACTIONS.traceSubmitEditedOrderApiResponseSuccess,
          ),
        ),
        this.actions$.pipe(
          ofType(ORDER_TRACING_ACTIONS.endOrderSubmissionTrace),
        ),
      ]).pipe(
        tap(submittedOrderAndNewOrderArr => {
          const [submittedOrder, receivedOrder] = submittedOrderAndNewOrderArr;
          const newOrder = receivedOrder.order;
          let allProductNumbers: number[] = [];
          const submittedOrderId =
            submittedOrder?.tracking?.tracing?.data?.attributes?.order[
              'orderId'
            ];
          const submitOrderTraceArr = JSON.parse(
            sessionStorage.getItem('submitOrderTrace'),
          ) || [null, true, true];
          const [sessionOrder, isWebSocket, traceDispatched] =
            submitOrderTraceArr;

          if (
            !!submittedOrderId &&
            !!sessionOrder &&
            traceDispatched === false &&
            (submittedOrderId === newOrder?.orderHeader?.origOrderId ||
              submittedOrderId === newOrder?.orderHeader?.orderId ||
              submittedOrderId === newOrder?.orderHeader?.origSplitOrderId) &&
            (sessionOrder.orderId === newOrder?.orderHeader?.orderId ||
              sessionOrder.orderId ===
                newOrder?.orderHeader?.origSplitOrderId ||
              sessionOrder.orderId === newOrder?.orderHeader?.origOrderId) &&
            (newOrder?.orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
              newOrder?.orderHeader?.orderStatus ===
                OrderStatus.SUBMITTED_WITH_EXCEPTIONS ||
              newOrder?.orderHeader?.orderStatus === OrderStatus.ORDER_ERROR ||
              newOrder?.orderHeader?.orderStatus ===
                OrderStatus.EDITING_SUBMITTED)
          ) {
            sessionStorage.setItem(
              'submitOrderTrace',
              JSON.stringify([sessionOrder, isWebSocket, true]),
            );
            const incomingOrderItems = newOrder?.orderItems;
            const submittedOrderProducts =
              submittedOrder?.tracking?.tracing?.data?.attributes?.products;

            incomingOrderItems.map(item =>
              allProductNumbers.push(item?.productNumber),
            );
            (
              submittedOrder?.tracking?.tracing?.data?.attributes
                ?.products as Product[]
            ).map(item => allProductNumbers.push(item?.productNumber));
            allProductNumbers = Array.from(
              new Set(allProductNumbers.filter(item => item !== undefined)),
            );
            this.store
              .select(selectProductAttributesByIds(allProductNumbers))
              .pipe(
                take(1),
                map(productMap => {
                  const incomingOrderProducts =
                    SharedTrackingService.tracingProductData(
                      incomingOrderItems,
                      productMap,
                    );

                  const trackingData = {
                    tracing: {
                      data: {
                        isEndOfTrace: true,
                        isStartOfTrace: false,
                        traceContext: 'order',
                        attributes: {
                          event: !!submittedOrder?.tracking?.tracing?.data
                            ?.attributes?.order['tandemOrderId']
                            ? OrderTracingMessagesTest.editOrderEvent
                            : OrderTracingMessagesTest.submitOrderEvent,
                          order: {
                            traceContext: 'order',
                            orderId:
                              newOrder?.orderHeader?.orderId || undefined,
                            origOrderId:
                              newOrder?.orderHeader?.origOrderId || undefined,
                            origSplitOrderId:
                              newOrder?.orderHeader?.origSplitOrderId ||
                              undefined,
                            tandemOrderNumber:
                              newOrder?.orderHeader?.tandemOrderNumber,
                            requestedDeliveryDate:
                              newOrder?.orderHeader?.requestedDeliveryDate,
                            confirmedDeliveryDate:
                              newOrder?.orderHeader?.confirmedDeliveryDate,
                            totalAmount: newOrder?.orderHeader?.totalDollars,
                            openMethod:
                              submittedOrder?.tracking?.tracing?.data
                                ?.attributes?.orderStatus ===
                              OrderStatus.EDITING_SUBMITTED
                                ? OrderSubmittedTracingMessages.openMethod
                                : 'N/A',
                            isWebsocket:
                              isWebSocket || receivedOrder.isWebsocket,
                          },
                          webSocketOrderProducts: incomingOrderProducts,
                          submittedOrderProducts: submittedOrderProducts,
                          errorDetails:
                            newOrder?.orderHeader?.errorDetails || undefined,
                        },
                      },
                    },
                  } as Tracking;
                  this.orderTracingService.trackSubmitOrderWebsocket(
                    trackingData,
                  );
                }),
              )
              .subscribe();
          }
        }),
      ),
    { dispatch: false },
  );

  createNewOrderAfterCustomerRelatedDataLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ORDER_ACTIONS.createNewOrderAfterCustomerRelatedDataLoaded),
        exhaustMap(payload => {
          return combineLatest([
            this.panAppState.customer$.pipe(
              filter(
                cust => cust.customerNumber === payload.customer.customerNumber,
              ),
            ),
            this.inspiredPicksStoreService.inspiredPicks$,
            this.didYouForgetStoreService.didYouForget$,
            this.deliveryStoreService.nextDelivery$,
          ]).pipe(
            // Skip the first emmission as no api calls have been made yet
            skip(1),
            filter(([customer, inspiredPicks, didYouForget, nextDelivery]) => {
              const hasLoadingError =
                (!inspiredPicks.loading && inspiredPicks.error) ||
                (!didYouForget.loading && didYouForget.error) ||
                (!nextDelivery.loading && nextDelivery.error);

              return (
                hasLoadingError ||
                (!inspiredPicks.loading &&
                  !didYouForget.loading &&
                  !nextDelivery.loading)
              );
            }),
            take(1),
            mergeMap(
              ([customer, inspiredPicks, didYouForget, nextDelivery]) => {
                const errorLoadingRelatedData =
                  inspiredPicks.error ||
                  didYouForget.error ||
                  nextDelivery.error;
                // When no error encountered create the new order
                if (!errorLoadingRelatedData) {
                  this.orderService.createNewOrderAndNav();
                } else {
                  // Log the error so log rocket picks it up
                  console.error(
                    `Error calling for related data on Customer: ${customer.customerNumber}. InspiredPicks: ${inspiredPicks.error}, DidYouForget: ${didYouForget.error}, NextDelivery: ${nextDelivery.error}`,
                  );
                }
                return of({});
              },
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  trackCancelSubmittedOrder$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(
          ofType(
            CART_ACTIONS.cancelSubmittedCartSuccess,
            MY_ORDERS_ACTIONS.cancelOrderAsync,
          ),
        ),
        this.actions$.pipe(ofType(MY_ORDERS_ACTIONS.getCustomerOrdersSuccess)),
      ]).pipe(
        delay(1000),
        filter(([payload, customerPayload]) => {
          if ('orderTransactions' in payload) {
            return (
              !!payload &&
              sessionStorage.getItem('cancelSubmittedOrder') === 'true'
            );
          } else if ('order' in payload) {
            const orderCancelled = customerPayload.orders?.find(
              order =>
                payload.order?.orderHeader?.orderId ===
                order?.orderHeader?.orderId,
            );
            return (
              (!orderCancelled ||
                orderCancelled?.orderHeader?.orderStatus ===
                  OrderStatus.CANCELLED ||
                orderCancelled?.orderHeader?.orderStatus ===
                  OrderStatus.CANCELLING ||
                orderCancelled?.orderHeader?.orderStatus ===
                  OrderStatus.DELETED ||
                orderCancelled?.orderHeader?.orderStatus ===
                  OrderStatus.TANDEM_DELETED) &&
              sessionStorage.getItem('cancelSubmittedOrder') === 'true'
            );
          } else return false;
        }),
        tap(([payload, customerPayload]) => {
          let order: any = {};
          if ('orderTransactions' in payload) {
            //FROM WEBSOCKET
            order = {
              orderId: payload.orderTransactions[0]?.orderId || undefined,
              origOrderId:
                payload.orderTransactions[0]?.origOrderId || undefined,
              origSplitOrderId:
                payload.orderTransactions[0]?.origSplitOrderId || undefined,
              tandemOrderNumber:
                payload.orderTransactions[0]?.tandemOrderNumber,
              requestedDeliveryDate:
                payload.orderTransactions[0]?.requestedDeliveryDate,
              confirmedDeliveryDate:
                payload.orderTransactions[0]?.confirmedDeliveryDate,
              totalAmount: payload.orderTransactions[0]?.totalDollars,
              errorDetails:
                payload.orderTransactions[0]?.errorDetails || undefined,
              isWebSocket: true,
            };
          } else if ('order' in payload) {
            //FROM API
            order = {
              orderId: payload.order?.orderHeader?.orderId || undefined,
              origOrderId: payload.order?.orderHeader?.origOrderId || undefined,
              origSplitOrderId:
                payload.order?.orderHeader?.origSplitOrderId || undefined,
              tandemOrderNumber: payload.order?.orderHeader?.tandemOrderNumber,
              requestedDeliveryDate:
                payload.order?.orderHeader?.requestedDeliveryDate,
              confirmedDeliveryDate:
                payload.order?.orderHeader?.confirmedDeliveryDate,
              totalAmount: payload.order?.orderHeader?.totalDollars,
              errorDetails:
                payload.order?.orderHeader?.errorDetails || undefined,
              isWebSocket: false,
            };
          } else return;
          const trackingData = {
            tracing: {
              data: {
                isEndOfTrace: true,
                isStartOfTrace: false,
                traceContext: OrderTracingMessages.cancelSubmittedOrderContext,
                attributes: {
                  event: OrderTracingMessages.cancelSubmittedOrder,
                  order,
                },
              },
            },
          } as Tracking;
          sessionStorage.removeItem('cancelSubmittedOrder');
          this.orderTracingService.trackCancelSubmittedOrderWebsocket(
            trackingData,
            !!order.errorDetails,
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private orderTracingService: OrderTracingService,
    private store: Store<any>,
    private orderItemDtoService: OrderItemDTOService,
    private orderDtoService: OrderDtoService,
    private deliveryStoreService: DeliveryStoreService,
    private didYouForgetStoreService: DidYouForgetStoreService,
    private inspiredPicksStoreService: InspiredPicksStoreService,
    private orderService: OrderService,
    private panAppState: PanAppState,
  ) {}
}
